import { useEffect, useState } from 'react'
import { AlertCircle, Bot, Loader2 } from 'lucide-react'
import type {
  SystemIssueMessage,
  SystemIssueMessageIcon,
} from '../../types/system-issues'
import { Avatar } from '../../../occurrences/components/common/Avatar'
import { systemIssuesService } from '../../../../services/systemIssuesService'
import { linkifyText } from '../../../occurrences/utils/linkify'
import { ImageLightbox } from '../../../../components/chat/ImageLightbox'

interface IssueMessageItemProps {
  message: SystemIssueMessage
  /** Mensagem do usuário logado: balão à direita, sem avatar/nome (estilo chat). */
  isOwn?: boolean
}

const SYSTEM_ICONS: Record<
  SystemIssueMessageIcon,
  React.ComponentType<{ className?: string }>
> = {
  bot: Bot,
  alert: AlertCircle,
}

function formatTime(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface AttachmentThumbnailProps {
  messageId: number
  index: number
  attachment: { name: string; size: number; type: string; url?: string }
}

/**
 * Anexos aqui são sempre imagens — é o único tipo que o upload aceita (ver
 * `SystemIssuesUploadsService.prepareUpload`) — por isso já resolve a URL
 * assinada ao montar (em vez de só no clique, como o download genérico de
 * Ocorrências) e mostra uma miniatura. Clicar abre em tamanho grande via
 * `ImageLightbox`, reaproveitando a mesma URL já resolvida.
 */
function AttachmentThumbnail({
  messageId,
  index,
  attachment,
}: AttachmentThumbnailProps) {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(
    attachment.url ?? null,
  )
  const [failed, setFailed] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  useEffect(() => {
    if (resolvedUrl) return
    let cancelled = false
    systemIssuesService
      .getAttachmentDownloadUrl(messageId, index)
      .then((url) => {
        if (!cancelled) setResolvedUrl(url)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [messageId, index, resolvedUrl])

  if (failed) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-[5px] border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-1 text-xs text-red-700 dark:text-red-300"
        title={attachment.name}
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Falha ao carregar {attachment.name}
      </span>
    )
  }

  if (!resolvedUrl) {
    return (
      <div className="flex h-16 w-16 items-center justify-center rounded-[5px] border border-border-subtle bg-surface-muted">
        <Loader2 className="h-4 w-4 animate-spin text-subtle" aria-hidden />
      </div>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        title={`Abrir ${attachment.name} (${formatBytes(attachment.size)})`}
        className="h-16 w-16 shrink-0 overflow-hidden rounded-[5px] border border-border-subtle transition hover:border-blue-300 dark:hover:border-blue-800"
      >
        <img
          src={resolvedUrl}
          alt={attachment.name}
          className="h-full w-full object-cover"
        />
      </button>
      {lightboxOpen && (
        <ImageLightbox
          src={resolvedUrl}
          alt={attachment.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  )
}

/**
 * Versão simplificada de `MessageItem` de Ocorrências: sem menções, sem
 * card de ocorrência estruturada, sem citação (reply) — nenhum desses
 * conceitos existe em Problemas Reportados. Mantém: avatar/nome/hora,
 * mensagens de sistema (log de status/atribuição) e anexos. Layout de chat:
 * as próprias mensagens ficam à direita, as dos outros à esquerda.
 */
export function IssueMessageItem({
  message,
  isOwn = false,
}: IssueMessageItemProps) {
  if (message.kind === 'system') {
    const Icon = message.system_icon ? SYSTEM_ICONS[message.system_icon] : Bot
    return (
      <div
        data-message-id={message.id}
        className="rounded-[5px] border border-blue-100 dark:border-blue-900/40 bg-[#eff6ff] dark:bg-blue-900/20 px-3 py-2 text-xs text-blue-900 dark:text-blue-200"
      >
        <div className="flex items-start gap-2">
          <Icon className="h-3.5 w-3.5 shrink-0 text-[#2563eb] dark:text-blue-400" />
          <span className="whitespace-pre-wrap">
            {linkifyText(message.content)}
          </span>
        </div>
      </div>
    )
  }

  const authorName = message.author?.full_name || 'Usuário'
  const picture = message.author?.picture ?? null

  return (
    <div
      data-message-id={message.id}
      className={`flex items-end gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      {!isOwn && <Avatar name={authorName} picture={picture} size={28} />}
      <div
        className={`min-w-0 max-w-[75%] rounded-2xl border px-3 py-2 ${
          isOwn
            ? 'rounded-br-sm border-blue-100 bg-[#eff6ff] dark:border-blue-900/40 dark:bg-blue-900/30'
            : 'rounded-bl-sm border-border-subtle bg-surface-muted'
        }`}
      >
        {!isOwn && (
          <div className="flex items-center gap-2 text-xs">
            <strong className="text-body">{authorName}</strong>
            {message.author?.role && (
              <span className="text-subtle">{message.author.role}</span>
            )}
          </div>
        )}
        {message.content && (
          <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-body/90">
            {linkifyText(message.content)}
          </p>
        )}
        {message.attachments && message.attachments.length > 0 && (
          <ul
            className={`mt-2 flex flex-wrap gap-1.5 ${isOwn ? 'justify-end' : ''}`}
          >
            {message.attachments.map((att, idx) => (
              <li key={`${message.id}-${idx}`}>
                <AttachmentThumbnail
                  messageId={message.id}
                  index={idx}
                  attachment={att}
                />
              </li>
            ))}
          </ul>
        )}
        <time
          dateTime={message.created_at}
          className="mt-1 block text-right text-[10px] text-subtle"
        >
          {formatTime(message.created_at)}
        </time>
      </div>
    </div>
  )
}
